import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { NotificationSettings as NotificationSettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

interface NotificationSettingsProps {
  settings: NotificationSettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<NotificationSettingsType>(settings);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      emailTemplates: {
        ...prev.emailTemplates,
        [name]: value
      }
    }));
  };

  const handleAddEmail = () => {
    if (newEmail && !formData.adminEmails.includes(newEmail)) {
      setFormData(prev => ({
        ...prev,
        adminEmails: [...prev.adminEmails, newEmail]
      }));
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setFormData(prev => ({
      ...prev,
      adminEmails: prev.adminEmails.filter(e => e !== email)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('notification', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات الإشعارات');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات الإشعارات
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enableEmailNotifications}
                onChange={handleChange}
                name="enableEmailNotifications"
                disabled={!editing}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1 }} />
                إشعارات البريد الإلكتروني
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enableSmsNotifications}
                onChange={handleChange}
                name="enableSmsNotifications"
                disabled={!editing}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SmsIcon sx={{ mr: 1 }} />
                إشعارات الرسائل النصية
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enablePushNotifications}
                onChange={handleChange}
                name="enablePushNotifications"
                disabled={!editing}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NotificationsIcon sx={{ mr: 1 }} />
                إشعارات التطبيق
              </Box>
            }
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        إعدادات الإشعارات
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifyOnNewOrder}
                onChange={handleChange}
                name="notifyOnNewOrder"
                disabled={!editing}
              />
            }
            label="إشعار عند طلب جديد"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifyOnOrderStatusChange}
                onChange={handleChange}
                name="notifyOnOrderStatusChange"
                disabled={!editing}
              />
            }
            label="إشعار عند تغيير حالة الطلب"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifyOnLowStock}
                onChange={handleChange}
                name="notifyOnLowStock"
                disabled={!editing}
              />
            }
            label="إشعار عند انخفاض المخزون"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          بريد المسؤولين
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="إضافة بريد إلكتروني جديد"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={!editing}
                placeholder="example@domain.com"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAddEmail}
                        disabled={!editing || !newEmail}
                        edge="end"
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.adminEmails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={editing ? () => handleRemoveEmail(email) : undefined}
                color="primary"
                variant="outlined"
              />
            ))}

            {formData.adminEmails.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                لم يتم إضافة أي بريد إلكتروني بعد
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          قوالب البريد الإلكتروني
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            يمكنك استخدام {'{{orderNumber}}'} للإشارة إلى رقم الطلب في القوالب
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="قالب تأكيد الطلب"
                name="orderConfirmation"
                value={formData.emailTemplates.orderConfirmation}
                onChange={handleTemplateChange}
                disabled={!editing}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="قالب شحن الطلب"
                name="orderShipped"
                value={formData.emailTemplates.orderShipped}
                onChange={handleTemplateChange}
                disabled={!editing}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="قالب تسليم الطلب"
                name="orderDelivered"
                value={formData.emailTemplates.orderDelivered}
                onChange={handleTemplateChange}
                disabled={!editing}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="قالب إلغاء الطلب"
                name="orderCancelled"
                value={formData.emailTemplates.orderCancelled}
                onChange={handleTemplateChange}
                disabled={!editing}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setEditing(false);
              setNewEmail('');
            }}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NotificationSettings;
